/**
 * This file is a holding bag of all data related functions. We should consider
 * starting to break this data out into seperate files once we have a better
 * idea of how things might fit together.
 */

import { SalesForceUserLookupResponse } from "./salesforce";

export enum EyeTestCode {
  "less_6_months" = "less_6_months",
  "6_12_months" = "6_12_months",
  "1_2_years" = "1_2_years",
  "over_2_years" = "over_2_years",
  "unknown_never" = "unknown_never",
}

export type PatientEnrolmentFormData = {
  title: string;
  first_name: string;
  surname: string;
  dob: string;
  postcode: string;
  indigenous: string;
  ndss: string;
  hba1c: string;
  last_eye_test: EyeTestCode | "";
  follow_up_sms: boolean;
  follow_up_sms_number: string;
  follow_up_email: boolean;
  follow_up_email_address: string;
  follow_up_post: boolean;
  follow_up_post_address: string;
  patient_consent: boolean;
  recaptcha_token: string | null;
};

// Default data for the patient signup form
export const defaultData: PatientEnrolmentFormData = {
  title: "",
  first_name: "",
  surname: "",
  dob: "",
  postcode: "",
  indigenous: "",
  ndss: "",
  hba1c: "",
  last_eye_test: "",
  follow_up_sms: false,
  follow_up_sms_number: "",
  follow_up_email: false,
  follow_up_email_address: "",
  follow_up_post: false,
  follow_up_post_address: "",
  patient_consent: false,
  recaptcha_token: null,
};

/**
 * Takes patient lookup data from Salesforce and combines it with the default
 * form. This is used to automatically populate the form in the activation
 * flow.
 */
export const buildDefaultWithExtraData = (
  salesForceData: SalesForceUserLookupResponse
) => {
  return {
    ...defaultData,
    ...salesForceData,
    ...{
      has_ndss: salesForceData.ndss ? "yes" : "no",
    },
  };
};

export const eyeCheckDue = (eyeTestCode: EyeTestCode) => {
  switch (eyeTestCode) {
    case EyeTestCode["less_6_months"]:
      return false;
    case EyeTestCode["6_12_months"]:
      return false;
    case EyeTestCode["1_2_years"]:
      return false;
    case EyeTestCode["over_2_years"]:
      return true;
    case EyeTestCode["unknown_never"]:
      return true;
    default:
      return true;
  }
};

/**
 * Produces a nice label for the eye check code.
 */
export const eyeTestCodeToLabel = (eyeTestCode: EyeTestCode) => {
  switch (eyeTestCode) {
    case EyeTestCode["less_6_months"]:
      return "less than 6 months ago";
    case EyeTestCode["6_12_months"]:
      return "6 to 12 months ago";
    case EyeTestCode["1_2_years"]:
      return "1 to 2 years ago";
    case EyeTestCode["over_2_years"]:
      return "over 2 years ago";
    case EyeTestCode["unknown_never"]:
      return "don't know / never";
    default:
      return "don't know / never";
  }
};
